// src/components/Dashboard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { formatCurrency } from '../utils/formatting';
import RunwayCard from './RunwayCard';
import ProjectionChart from './ProjectionChart';
import FunMoneyCard from './FunMoneyCard';
import LoadingSpinner from './LoadingSpinner';

const Dashboard = () => {
    const {
        loading,
        error,
        runwayData,
        projectionData,
        funMoneyData,
        refreshData,
        lastUpdated
    } = useAppContext();

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <LoadingSpinner size="large" message="Loading your financial data..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-100 text-red-700 border border-red-200 rounded-md mb-4">
                <h3 className="font-bold mb-2">Error Loading Data</h3>
                <p>{error}</p>
                <button
                    onClick={refreshData}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Financial Runway</h1>
                <button
                    onClick={refreshData}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Refresh Data
                </button>
            </div>

            {lastUpdated && (
                <div className="text-sm text-gray-500 mb-6">
                    Last updated: {new Date(lastUpdated).toLocaleString()}
                </div>
            )}

            {runwayData && (
                <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                    <h2 className="text-lg font-semibold mb-3">Monthly Cashflow</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-3 bg-green-50 rounded-md">
                            <div className="text-sm text-green-800 mb-1">Monthly Income</div>
                            <div className="text-xl font-bold">{formatCurrency(runwayData.monthly_income)}</div>
                        </div>

                        <div className="p-3 bg-red-50 rounded-md">
                            <div className="text-sm text-red-800 mb-1">Monthly Expenses</div>
                            <div className="text-xl font-bold">{formatCurrency(runwayData.monthly_expenses)}</div>
                        </div>

                        <div className="p-3 bg-blue-50 rounded-md">
                            <div className="text-sm text-blue-800 mb-1">Net Savings</div>
                            <div className={`text-xl font-bold ${runwayData.monthly_burn_rate < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {formatCurrency(runwayData.monthly_burn_rate)}
                            </div>
                            <div className="text-xs text-gray-600">
                                {runwayData.monthly_burn_rate < 0
                                    ? `This means you're spending ${formatCurrency(Math.abs(runwayData.monthly_burn_rate))} more than you earn each month.`
                                    : `This means you're saving ${formatCurrency(runwayData.monthly_burn_rate)} each month.`
                                }
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Then the original projectionData section follows */}
            {projectionData && (
                <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Balance Projection</h2>
                    <ProjectionChart
                        projectionData={projectionData.projection}
                        targetAmount={projectionData.target_amount}
                        minimumThreshold={projectionData.minimum_threshold}
                    />
                    <div className="mt-4 text-sm text-gray-600">
                        <p>{projectionData.message}</p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Fun Money Card */}
                {funMoneyData && (
                    <div className="bg-white rounded-lg shadow-md p-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Fun Money</h2>
                            <Link
                                to="/fun-money"
                                className="text-blue-600 hover:text-blue-800"
                            >
                                View Details
                            </Link>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <FunMoneyCard
                                person="you"
                                name={funMoneyData.you.name}
                                balance={funMoneyData.you.current_balance}
                                allowance={funMoneyData.you.monthly_allowance}
                            />

                            <FunMoneyCard
                                person="spouse"
                                name={funMoneyData.spouse.name}
                                balance={funMoneyData.spouse.current_balance}
                                allowance={funMoneyData.spouse.monthly_allowance}
                            />
                        </div>

                        {runwayData && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-md">
                                <p className="text-sm text-blue-800">
                                    Safe monthly discretionary spending:
                                    <span className="font-bold ml-1">
                                        {formatCurrency(runwayData.safe_monthly_discretionary)}
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow-md p-4">
                    <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
                    <div className="grid grid-cols-1 gap-3">
                        <Link
                            to="/add-transaction"
                            className="px-4 py-3 bg-green-600 text-white rounded text-center hover:bg-green-700"
                        >
                            Add Fun Money Transaction
                        </Link>

                        <Link
                            to="/spending-analysis"
                            className="px-4 py-3 bg-purple-600 text-white rounded text-center hover:bg-purple-700"
                        >
                            Spending Analysis
                        </Link>

                        <Link
                            to="/settings"
                            className="px-4 py-3 bg-gray-600 text-white rounded text-center hover:bg-gray-700"
                        >
                            Adjust Settings
                        </Link>
                    </div>
                </div>
            </div>

            {runwayData && runwayData.accounts && runwayData.accounts.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-4 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Accounts</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">Account</th>
                                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-right text-xs font-semibold text-gray-600 uppercase">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runwayData.accounts.map((account, index) => (
                                    <tr key={index}>
                                        <td className="py-2 px-4 border-b border-gray-200">{account.name}</td>
                                        <td className="py-2 px-4 border-b border-gray-200 text-right font-mono">
                                            {formatCurrency(account.balance)}
                                        </td>
                                    </tr>
                                ))}
                                <tr className="bg-gray-50">
                                    <td className="py-2 px-4 border-b border-gray-200 font-bold">Total</td>
                                    <td className="py-2 px-4 border-b border-gray-200 text-right font-bold font-mono">
                                        {formatCurrency(runwayData.current_amount)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;